// src/utils/impersonation.ts
// Downward impersonation (GitLab-style): an Administrator/AdvancedUser browses
// the whole KYC Admin as a lower-role user. The swap happens server-side per
// request (the API validates the REAL token's role and replaces the
// principal), so the UI only keeps which subject to act as and sends it as a
// header; it never mints or stores another user's token. Session-scoped on
// purpose: impersonation is a per-tab support action and must not leak into
// other tabs or survive the browser closing.

export const IMPERSONATION_HEADER = 'X-Impersonate-Subject';

const STORAGE_KEY = 'impersonation';

/**
 * Strict hierarchy mirroring ImpersonationMiddleware's RoleRank on the API. Only
 * Administrator and AdvancedUser may impersonate at all; NoAccess has no rank
 * (defaults to 0) and can neither impersonate nor be impersonated.
 */
const ROLE_RANK: Record<string, number> = {
  NoAccess: 0,
  User: 1,
  AdvancedUser: 2,
  Administrator: 3,
};

const IMPERSONATION_ELIGIBLE_RANK = ROLE_RANK.AdvancedUser;

export interface ImpersonationState {
  /** Identity-provider subject (user id) being impersonated. */
  sub: string;
  /** What the app-bar banner shows (the target's email). */
  displayName: string;
  /** The target's role — becomes the effective role app-wide. */
  role: string;
}

/** Minimal shape needed from a users-list record to evaluate the impersonation guard. */
export interface ImpersonationTarget {
  id: string;
  role: string;
  enabled: boolean;
}

export const getImpersonation = (): ImpersonationState | null => {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationState;
  } catch {
    return null;
  }
};

export const startImpersonation = (state: ImpersonationState): void => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

export const stopImpersonation = (): void => {
  sessionStorage.removeItem(STORAGE_KEY);
};

/**
 * Mirror of the API's guard (strictly downward, enabled, never self, never
 * nested) so the button only shows where the request would succeed.
 */
export const canImpersonate = (
  actorRole: string | undefined,
  actorSub: string | undefined,
  target: ImpersonationTarget
): boolean => {
  if (getImpersonation()) return false;
  if (!actorRole || !actorSub) return false;
  const actorRank = ROLE_RANK[actorRole] ?? 0;
  if (actorRank < IMPERSONATION_ELIGIBLE_RANK) return false;
  const targetRank = ROLE_RANK[target.role] ?? 0;
  if (!target.enabled || targetRank === 0) return false;
  if (target.id === actorSub) return false;
  return actorRank > targetRank;
};
